import { DatePipe, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';

import { Store } from '@ngrx/store';

import { OrderChangeHistoryComponent } from '@components/orders/manage-orders/order-change-history/order-change-history.component';
import { OrderItemProcessesComponent } from '@components/orders/manage-orders/order-item-processes/order-item-processes.component';
import { OrderJobDetailsComponent } from '@components/orders/manage-orders/order-job-details/order-job-details.component';
import { OrderJobFollowUpComponent } from '@components/orders/manage-orders/order-job-follow-up/order-job-follow-up.component';
import { OrderManageGeneralComponent } from '@components/orders/manage-orders/order-manage-general/order-manage-general.component';
import { OrderNoteHistoryComponent } from '@components/orders/manage-orders/order-note-history/order-note-history.component';
import { ProductionFlowComponent } from '@components/orders/manage-orders/production-flow/production-flow.component';

import {
  ConfirmationDialogComponent,
  ConfirmationDialogData,
} from '../../../@core/dialogs/confirmation-dialog/confirmation-dialog.component';
import { OrdersActions } from '../../../@store/orders/orders.actions';
import { ConstructionStepPayload, singleOrderDetails } from '../../../@store/orders/orders.model';
import {
  selectOrderDetails,
  selectOrderError,
  selectOrderLoading,
  selectOrderSaving,
  selectUpdatedOrderError,
} from '../../../@store/orders/orders.selectors';
import { CONFIG } from '../../../config';
import { SafeAny } from '../../../types/safe-any';

import { BackToOrdersListDialogComponent } from './back-to-orders-list-dialog/back-to-orders-list-dialog.component';

@Component({
  selector: 'ciwc-manage-orders',
  imports: [
    OrderManageGeneralComponent,
    ReactiveFormsModule,
    MatCardModule,
    DatePipe,
    OrderJobDetailsComponent,
    MatProgressSpinnerModule,
    NgIf,
    ProductionFlowComponent,
    OrderItemProcessesComponent,
    OrderJobFollowUpComponent,
    OrderNoteHistoryComponent,
    OrderChangeHistoryComponent,
    MatButton,
    RouterLink,
  ],
  standalone: true,
  templateUrl: './manage-orders.component.html',
  styleUrl: './manage-orders.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageOrdersComponent implements OnInit {
  readonly selectedOrder = this.store.selectSignal(selectOrderDetails);
  readonly selectedOrderLoading = this.store.selectSignal(selectOrderLoading);
  readonly selectedOrderError = this.store.selectSignal(selectOrderError);
  readonly selectUpdatedOrderError = this.store.selectSignal(selectUpdatedOrderError);
  readonly selectedOrderSaving = this.store.selectSignal(selectOrderSaving);

  private readonly destroyRef = inject(DestroyRef);
  private orderId: string | null = null;

  @ViewChild(OrderItemProcessesComponent) itemProcessesComponent!: OrderItemProcessesComponent;
  form: FormGroup;
  generalForm: FormGroup;
  order: SafeAny = null;
  errorText = CONFIG.ERROR_MESSAGES.DEFAULT;
  boundCreateShipmentGroup: () => FormGroup;

  readonlySpecFields = false;
  showConfirmModal = true;
  initiateRedirect = true;
  isManageOrders = true;
  isLocked = false;
  isSaving = signal(false);
  setFormValues = signal(false);

  // store construction steps
  constructionSteps: ConstructionStepPayload[] = [];

  constructor(
    private fb: FormBuilder,
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
  ) {
    effect(() => {
      this.order = this.selectedOrder() || null;

      if (this.order) {
        this.patchOrderForm(this.order);
        this.getOrderDetails();
        this.setFormValues.set(true);
      }

      if (this.selectUpdatedOrderError()) {
        this.isSaving.set(false);
      }
    });
    this.generalForm = this.fb.group({
      assigneeId: [null],
      status: [null],
      description: [''],
      clientNotes: [''],
      client: [null],
      clientsId: [null],
    });
    this.form = this.fb.group({
      general: this.generalForm,
      caCustomDescription: [''],
      clientPartNo: [''],
      feetPerReel: [''],
      constructionSteps: [null],
      jobDetails: this.fb.group({
        clientPurchaseOrder: [''],
        pricePerUnit: [null],
        orderTotalPrice: [null],
        totalQuantity: [null],
        balanceToFollow: [null],
        overage: [null],
        weeksAmount: [null],
        promisedDate: [null],
        expeditedOrder: [false],
        expediteDate: [null],
        expeditePrice: [null],
        faiReport: [false],
        faiReportPrice: [null],
        lastShipmentAmount: [{ value: null, disabled: true }],
        testReports: [false],
        certsRequired: [false],
        cfm: [false],
        ciwToSupplyWire: [false],
        isComplete: [false],
        shippingDetailsText: [''],
        followUps: this.fb.array([this.createFollowUpGroup()]),
      }),
      shippingDetails: this.fb.group({
        address1Street: [''],
        address1StreetDetails: [''],
        address1City: [''],
        address1Zip: [''],
        address1Country: [''],
        address1State: [''],
        attn: [''],
        shippingNotes: [''],
      }),
      shipments: this.fb.array([]),
      materials: this.fb.array([]),
      materialsReceived: [false],
      specification: this.fb.group({}),
      caNumbersId: [null],
    });
    this.boundCreateShipmentGroup = this.createShipmentGroup.bind(this);
  }

  ngOnInit(): void {
    this.getRouteParams();
    this.getOrderDetails();
    this.setFormCANumbersId();
  }

  patchOrderForm(order: singleOrderDetails) {
    // Patch caCustomDescription
    this.form.get('caCustomDescription')?.setValue(order.caCustomDescription || '');

    // Patch clientPartNo
    this.form.get('clientPartNo')?.setValue(order.details[0]?.clientPartNo || '');

    // Patch feetPerReel
    this.form.get('feetPerReel')?.setValue(order.details[0]?.feetPerReel || '');

    // Initialize construction steps if they exist in the order
    if (order.constructionSteps && order.constructionSteps.length > 0) {
      // Transform the steps to match the required format
      const initialSteps: ConstructionStepPayload[] = order.constructionSteps.map((step) => ({
        id: step.id,
        clientPartNo: step.clientPartNo || '',
        awg: step.awg || [],
        stranding: step.stranding || [],
        wireType: step.wireType || [],
        supplier: [], // Initialize required arrays
        finishedDiameter: [],
        tip: [],
        die: [],
        putUp: [],
        jacketMaterial: [],
        jacketColor: [],
        jacketPrinting: [],
        wallThickness: [],
        innerDiameter: [],
        outerDiameter: [],
        description: step.description || '',
        memo: '',
        feetPerReel: step.feetPerReel || '',
      }));

      this.constructionSteps = initialSteps;
      this.onConstructionStepsChange(initialSteps);
    }

    // Patch followUps
    const followUpsArray = this.followUps;
    followUpsArray.clear();
    if (order.followUps && order.followUps.length) {
      order.followUps.forEach((fu) => {
        followUpsArray.push(
          this.fb.group({
            dateFollowUp: [fu.dateFollowUp ? new Date(fu.dateFollowUp) : null],
            priorFirstDate: [fu.priorFirstDate ? new Date(fu.priorFirstDate) : null],
            priorSecondDate: [fu.priorSecondDate ? new Date(fu.priorSecondDate) : null],
            priorThirdDate: [fu.priorThirdDate ? new Date(fu.priorThirdDate) : null],
            notes: [fu.notes],
            ciw: [!!fu.ciw],
            cfm: [!!fu.cfm],
          }),
        );
      });
    } else {
      followUpsArray.push(this.createFollowUpGroup());
    }
  }

  get jobDetailsForm(): FormGroup {
    return this.form.get('jobDetails') as FormGroup;
  }

  createFollowUpGroup(): FormGroup {
    return this.fb.group({
      dateFollowUp: [null],
      priorFirstDate: [null],
      priorSecondDate: [null],
      priorThirdDate: [null],
      notes: [''],
      ciw: [false],
      cfm: [false],
    });
  }

  get followUps(): FormArray {
    return (this.form.get('jobDetails') as FormGroup).get('followUps') as FormArray;
  }

  addFollowUp() {
    this.followUps.push(this.createFollowUpGroup());
  }

  removeFollowUp(index: number) {
    this.followUps.removeAt(index);
  }

  createShipmentGroup(): FormGroup {
    return this.fb.group({
      shippingMethod: [''],
      trackingNumber: [''],
      unitsAvailableToShip: [''],
      units: [''],
      totalPrice: [0],
      weight: [''],
      dateShipped: [''],
      quickbooksInvoice: [''],
      shipmentPhoto: [''],
      shippingNotes: [''],
      dropShipment: [true],
      dropShipAddress: [''],
      dropStreetAddress2: [''],
      dropCity: [''],
      dropCountry: [''],
      dropState: [''],
      dropZip: [''],
      dropAttn: [''],
      dropShipBox: [''],
      dropShipAttachment: [''],
      packingSlip: [''],
      attachments: this.fb.array([]),
    });
  }

  get shipments(): FormArray {
    return this.form.get('shipments') as FormArray;
  }

  // handle construction steps changes
  onConstructionStepsChange(steps: ConstructionStepPayload[]) {
    this.constructionSteps = steps;
    this.form.get('constructionSteps')?.setValue(steps);
  }

  onSubmit() {
    if (!this.orderId) return;

    this.isSaving.set(true);
    this.showConfirmModal = false;

    const jobDetails = this.jobDetailsForm.value;
    const generalFormValue = this.generalForm.value;

    // Format dates to YYYY-MM-DD using local time
    const formatDate = (date: Date | null) => {
      if (!date) return null;
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const shippingDetailsValue = this.form.get('shippingDetails')?.value;
    const shippingDetails = shippingDetailsValue ? [shippingDetailsValue] : [];

    const shipmentsValue = this.form.get('shipments')?.value || [];
    const shipments = shipmentsValue.map(
      (shipment: {
        dateShipped: string | Date;
        dropShipment: boolean;
        packingSlip: string | null;
        unitsAvailableToShip: string | number;
        units: string | number;
        [key: string]: unknown;
      }) => ({
        ...shipment,
        dateShipped: shipment.dateShipped ? formatDate(new Date(shipment.dateShipped)) : null,
        dropShipment: shipment.dropShipment ? 1 : 0,
        packingSlip: shipment.packingSlip ? shipment.packingSlip : null,
        // Ensure types expected by backend
        unitsAvailableToShip:
          shipment.unitsAvailableToShip != null ? String(shipment.unitsAvailableToShip) : '',
        units: shipment.units != null ? String(shipment.units) : '',
        // Map attachments or other nested fields here if needed
      }),
    );

    const orderToSave = {
      ...generalFormValue,
      ...jobDetails,
      caCustomDescription: this.form.get('caCustomDescription')?.value,
      id: this.orderId,
      caNumbersId: this.form.get('caNumbersId')?.value,
      promisedDate: formatDate(jobDetails.promisedDate),
      expediteDate: formatDate(jobDetails.expediteDate),
      materialsReceived: this.form.get('materialsReceived')?.value || false,
      materials:
        this.form.get('materials')?.value?.map(
          (material: {
            id?: number;
            category: string;
            description: string;
            quantity: number;
            poNo: string;
            expDate: string;
            recDate: Date | string;
            status: string;
            notes: string;
            attachments?: {
              attachmentName: string;
              attachmentDocument: string;
            }[];
          }) => ({
            id: material.id || 0,
            category: material.category,
            description: material.description,
            quantity: material.quantity,
            poNo: material.poNo,
            expDate: material.expDate,
            recDate: formatDate(
              typeof material.recDate === 'string' ? new Date(material.recDate) : material.recDate,
            ),
            status: material.status,
            notes: material.notes,
            attachments:
              material.attachments?.map(
                (attachment: { attachmentName: string; attachmentDocument: string }) => ({
                  name: attachment.attachmentName,
                  document: attachment.attachmentDocument,
                }),
              ) || [],
          }),
        ) || [],
      followUps: this.followUps.value.map(
        (followUp: {
          id?: number;
          dateFollowUp: Date | null;
          priorFirstDate: Date | null;
          priorSecondDate: Date | null;
          priorThirdDate: Date | null;
          notes: string;
          ciw: boolean;
          cfm: boolean;
        }) => ({
          id: followUp.id || 0,
          dateFollowUp: formatDate(followUp.dateFollowUp),
          priorFirstDate: formatDate(followUp.priorFirstDate),
          priorSecondDate: formatDate(followUp.priorSecondDate),
          priorThirdDate: formatDate(followUp.priorThirdDate),
          notes: followUp.notes || '',
          ciw: followUp.ciw ? 1 : 0,
          cfm: followUp.cfm ? 1 : 0,
        }),
      ),
      itemProcesses: this.itemProcessesPayload,
      constructionSteps: this.form.get('constructionSteps')?.value,
      details: [
        {
          clientPartNo: this.form.get('clientPartNo')?.value,
          feetPerReel: this.form.get('feetPerReel')?.value,
          awg: this.form.get('specification')?.get('awg')?.value || [],
          stranding: this.form.get('specification')?.get('stranding')?.value || [],
          wireType: this.form.get('specification')?.get('wireType')?.value || [],
          supplier: this.form.get('specification')?.get('supplier')?.value || [],
          finishedDiameter: this.form.get('specification')?.get('finishedDiameter')?.value || [],
          tip: this.form.get('specification')?.get('tip')?.value || [],
          die: this.form.get('specification')?.get('die')?.value || [],
          putUp: this.form.get('specification')?.get('putUp')?.value || [],
          jacketMaterial: this.form.get('specification')?.get('jacketMaterial')?.value || [],
          jacketColor: this.form.get('specification')?.get('jacketColor')?.value || [],
          jacketPrinting: this.form.get('specification')?.get('jacketPrinting')?.value || [],
          wallThickness: this.form.get('specification')?.get('wallThickness')?.value || [],
          innerDiameter: this.form.get('specification')?.get('innerDiameter')?.value || [],
          outerDiameter: this.form.get('specification')?.get('outerDiameter')?.value || [],
          description: this.form.get('specification')?.get('description')?.value || '',
        },
      ],
      shippingDetails,
      shipments,
    };

    this.store.dispatch(
      OrdersActions.saveOrder({ order: orderToSave, initiateRedirect: this.initiateRedirect }),
    );
  }

  private mapConstructionFieldsSteps(
    step: ConstructionStepPayload,
    spec: Record<string, unknown>,
  ): Partial<ConstructionStepPayload> {
    const result: Partial<ConstructionStepPayload> = {
      description: step.description,
      memo: step.memo,
    };

    type FieldKey = keyof Pick<
      ConstructionStepPayload,
      | 'awg'
      | 'stranding'
      | 'wireType'
      | 'supplier'
      | 'finishedDiameter'
      | 'tip'
      | 'die'
      | 'putUp'
      | 'jacketMaterial'
      | 'jacketColor'
      | 'jacketPrinting'
      | 'wallThickness'
      | 'innerDiameter'
      | 'outerDiameter'
    >;

    const fields: FieldKey[] = [
      'awg',
      'stranding',
      'wireType',
      'supplier',
      'finishedDiameter',
      'tip',
      'die',
      'putUp',
      'jacketMaterial',
      'jacketColor',
      'jacketPrinting',
      'wallThickness',
      'innerDiameter',
      'outerDiameter',
    ];

    for (const field of fields) {
      const stepValue = step[field];
      const specValue = spec[field] as string[] | undefined;

      if (Array.isArray(stepValue)) {
        // Filter out values not in the specification
        const filteredValues = stepValue.filter((singleStep: string | null) => {
          return specValue?.includes(singleStep as string);
        });

        // Reorder the values to match the order in the specification
        const orderedValues = specValue?.filter((v) => filteredValues.includes(v));

        result[field] = orderedValues?.map((v) => v ?? null) ?? [];
      } else {
        result[field] = [];
      }
    }

    return result;
  }

  onGeneralCancel() {
    const dialogData: ConfirmationDialogData = {
      message: 'Are you sure you want to cancel these changes?',
      confirmText: 'YES',
      cancelText: 'NO',
    };

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: dialogData,
      disableClose: true,
      width: '500px',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: boolean) => {
        if (result) {
          this.showConfirmModal = false;
          this.router.navigate(['/orders']).then();
        }
      });
  }

  get itemProcessesPayload() {
    return this.itemProcessesComponent?.getItemProcessesPayload() || [];
  }

  private getRouteParams(): void {
    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      if (params['id']) {
        this.orderId = params['id'];
        this.store.dispatch(OrdersActions.loadOrder({ id: params['id'].toString(), isEdit: true }));
      }
    });
  }

  private getOrderDetails(): void {
    this.isLocked = !!this.order && !!this.order.isLocked;
    // Patch caNumbersId from order.caNumber.id if it exists
    if (this.order?.caNumber?.id) {
      this.form.get('caNumbersId')?.patchValue(this.order.caNumber.id);
    }

    if (this.order) {
      if (this.order.caNumber && this.order.caNumber.id) {
        this.form.get('caNumbersId')?.patchValue(this.order.caNumber.id);
      }

      if (this.isLocked) {
        const dialogRef = this.dialog.open(BackToOrdersListDialogComponent, {
          data: {
            lockedByUser: `${this.order.lockedByUser.firstName} ${this.order.lockedByUser.lastName}`,
          },
          disableClose: true,
          width: '550px',
        });

        dialogRef
          .afterClosed()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((result: boolean) => {
            if (result) {
              this.router.navigate(['/orders']).then();
            }
          });
      }
      this.patchOrderForm(this.order);
    }
  }

  private setFormCANumbersId(): void {
    this.form
      .get('caNumbersId')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((val) => {
        this.readonlySpecFields = !!val;
      });
  }
}
